import { useSearchParams } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { Expand } from 'iconoir-react'
import { useEffect, useMemo, useState } from 'react'
import AttributionTableFilter from '../analytics/AttributionTableFilter'
import SimpleTable from '../table/SimpleTable'
import { MarketingContactTable } from './MarketingContactTable'
import { FilterOperator, PredefinedFilter } from 'app/filter.utils'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { showModal } from 'app/lib/modal'
import { defaultBecameLeadDateFilter } from 'app/utils/defaultBecameLeadDateFilter'
import { AttributionRowType } from 'app/types/attribution'
import { defaultAttributionColumns } from 'app/table-columns/analytics/attribution'
import { defaultColumns } from 'app/table-columns/attribution-contacts'
import { hasRole, useHandleSearchParams, useUser } from 'app/utils'
import { isSMCOrg } from 'lib/isSMCOrg'

// observer: reads a feature toggle (and possibly authStore.user) in render; useFeatureToggle resolves async and its contract requires an observer wrapper (TRK-6020).
export const AttributionTable = observer(function AttributionTable({
  fromDate,
  toDate,
  integrationStatus,
  locationIds,
  sourceRows,
  mediumRows,
  sourceMediumRows,
  campaignRows,
  adCampaignRows,
  adPlatformRows,
  channelRows,
  reportedAttributionRows,
  enableExpand,
}: {
  fromDate: string
  toDate: string
  integrationStatus: string
  locationIds: string[]
  sourceRows?: AttributionRowType[]
  mediumRows?: AttributionRowType[]
  sourceMediumRows?: AttributionRowType[]
  campaignRows?: AttributionRowType[]
  adCampaignRows?: AttributionRowType[]
  adPlatformRows?: AttributionRowType[]
  channelRows?: AttributionRowType[]
  reportedAttributionRows?: AttributionRowType[]
  enableExpand?: boolean
}) {
  const user = useUser()
  const [searchParams] = useSearchParams()
  if (!user) return null
  const handleSearchParams = useHandleSearchParams()
  const staffV2Enabled = useFeatureToggle(`staff-v2`)
  const checkPermission = useCheckPermission()
  const [tableFilter, setTableFilter] = useState<
    | `Source`
    | `Medium`
    | `Source / Medium`
    | `Campaign`
    | `Ad Campaign`
    | `Ad Platform`
    | `Channel`
    | `Reported Attribution`
  >((searchParams.get(`tableFilter`) as any) ?? `Source / Medium`)
  const tableData = useMemo(() => {
    switch (tableFilter) {
      case `Source`:
        return sourceRows
      case `Medium`:
        return mediumRows
      case `Source / Medium`:
        return sourceMediumRows?.filter((row) => row.channel)
      case `Campaign`:
        return campaignRows
      case `Ad Campaign`:
        return adCampaignRows
      case `Ad Platform`:
        return adPlatformRows
      case `Channel`:
        return channelRows
      case `Reported Attribution`:
        return reportedAttributionRows
    }
  }, [tableFilter, sourceRows, mediumRows, sourceMediumRows, campaignRows, channelRows])

  useEffect(() => {
    let currentParam = searchParams.get(`tableFilter`)
    if (tableFilter === currentParam) return
    handleSearchParams({
      [`tableFilter`]: tableFilter ?? undefined,
      ...(searchParams.has(`filters`) ? { filters: undefined } : undefined),
    })
  }, [tableFilter])

  useEffect(() => {
    if (searchParams.has(`tableRow`)) {
      // oxlint-disable-next-line react/immutability -- existing closure or mutable API pattern is intentional
      handleTableRowClick(searchParams.get(`tableRow`) ?? ``, searchParams.get(`title`) ?? undefined)
    }
  }, [])

  const onTableModalClose = () => {
    if (searchParams.has(`title`)) {
      handleSearchParams({
        [`tableRow`]: undefined,
        [`title`]: undefined,
      })
    } else {
      handleSearchParams({
        [`tableRow`]: undefined,
      })
    }
  }

  const handleTableFilterChange = (
    filter:
      | `Source`
      | `Medium`
      | `Source / Medium`
      | `Campaign`
      | `Ad Campaign`
      | `Ad Platform`
      | `Channel`
      | `Reported Attribution`,
  ) => {
    setTableFilter(filter)
  }

  const handleTableRowClick = (bucket: string, title?: string) => {
    const searchParamsToAdd = {
      [`tableRow`]: bucket,
    }
    if (title) {
      searchParamsToAdd[`title`] = title
    }
    handleSearchParams(searchParamsToAdd)
    let defaultFilter: PredefinedFilter | undefined = undefined
    switch (tableFilter) {
      case `Source`:
      case `Medium`:
      case `Campaign`: {
        defaultFilter = {
          [`utm${tableFilter}.keyword`]: {
            operator: `exactly` as FilterOperator,
            value: bucket,
            readOnly: true,
          },
        }
        break
      }
      case `Source / Medium`: {
        defaultFilter = {
          [`utmSource.keyword`]: {
            operator: `exactly` as FilterOperator,
            value: bucket.split(` / `)[0],
            readOnly: true,
          },
          [`utmMedium.keyword`]: {
            operator: `exactly` as FilterOperator,
            value: bucket.split(` / `)[1],
            readOnly: true,
          },
        }
        break
      }
      case `Ad Campaign`: {
        defaultFilter = {
          [`trackableCampaignId`]: {
            operator: `exactly` as FilterOperator,
            value: bucket,
            readOnly: true,
          },
        }
        break
      }
      case `Ad Platform`: {
        defaultFilter = {
          [`platform`]:
            bucket === `Unknown`
              ? {
                  operator: `not_exactly` as FilterOperator,
                  value: `Any`,
                  readOnly: true,
                }
              : {
                  operator: `exactly` as FilterOperator,
                  value: bucket,
                  readOnly: true,
                },
        }
        break
      }
      case `Channel`: {
        defaultFilter = {
          [`channel.keyword`]:
            bucket === `Unknown`
              ? {
                  operator: `not_exactly` as FilterOperator,
                  value: `Any`,
                  readOnly: true,
                }
              : {
                  operator: `exactly` as FilterOperator,
                  value: bucket,
                  readOnly: true,
                },
        }
        break
      }
      case `Reported Attribution`: {
        defaultFilter = {
          [`reportedAttribution.keyword`]:
            bucket === `Unknown`
              ? {
                  operator: `not_exactly` as FilterOperator,
                  value: `Any`,
                  readOnly: true,
                }
              : {
                  operator: `exactly` as FilterOperator,
                  value: bucket,
                  readOnly: true,
                },
        }
        break
      }
    }
    defaultFilter = {
      ...defaultFilter,
      ...(searchParams.has(`fromDate`) && searchParams.has(`toDate`)
        ? {
            [`becameLeadDate`]: {
              operator: `between` as FilterOperator,
              value: [searchParams.get(`fromDate`)!, searchParams.get(`toDate`)!],
              readOnly: true,
            },
          }
        : defaultBecameLeadDateFilter),
    }
    showModal(
      <MarketingContactTable
        title={title ?? bucket}
        filterContactsBy="Leads"
        fromDate={fromDate}
        toDate={toDate}
        locationIds={locationIds}
        integrationStatus={integrationStatus}
        showDisqualified={true}
        rawChannel={
          tableFilter === `Channel` ? (bucket === `Unknown` ? `!channel:*` : `channel.keyword:"${bucket}"`) : undefined
        }
        utmSource={
          tableFilter === `Source`
            ? `utmSource.keyword:"${bucket}"`
            : tableFilter === `Source / Medium`
              ? `utmSource.keyword:"${bucket.split(` / `)[0]}"`
              : undefined
        }
        utmMedium={
          tableFilter === `Medium`
            ? `utmMedium.keyword:"${bucket}"`
            : tableFilter === `Source / Medium`
              ? `utmMedium.keyword:"${bucket.split(` / `)[1]}"`
              : undefined
        }
        utmCampaign={tableFilter === `Campaign` ? `utmCampaign.keyword:"${bucket}"` : undefined}
        trackableCampaignId={tableFilter === `Ad Campaign` ? `trackableCampaignId.keyword:"${bucket}"` : undefined}
        platform={tableFilter === `Ad Platform` ? (bucket === `Unknown` ? `!platform:*` : `"${bucket}"`) : undefined}
        reportedAttribution={
          tableFilter === `Reported Attribution`
            ? bucket === `Unknown`
              ? `!reportedAttribution:*`
              : `reportedAttribution:"${bucket}"`
            : undefined
        }
        defaultFilters={defaultFilter}
        customColumns={defaultColumns}
        onClose={() => onTableModalClose()}
      />,
      {
        hideTitle: true,
        removePadding: true,
        isFullScreen: true,
        onClose: () => onTableModalClose(),
        useRadix: true,
      },
    )
  }

  // Legacy role-based logic (staff-v2 OFF)
  const legacyShowAdColumns =
    [`Ad Campaign`, `Ad Platform`].includes(tableFilter) &&
    ((hasRole(user, `orgAdmin`) && isSMCOrg(user.orgId)) || !isSMCOrg(user.orgId))
  // Permission-based logic (staff-v2 ON)
  const permissionShowAdColumns =
    [`Ad Campaign`, `Ad Platform`].includes(tableFilter) && checkPermission(`org:marketing:advanced-filters`)
  const showAdColumns = staffV2Enabled ? permissionShowAdColumns : legacyShowAdColumns

  const columns = useMemo(
    () => defaultAttributionColumns(tableFilter, showAdColumns, isSMCOrg(user.orgId), handleTableRowClick),
    [user, tableFilter, handleTableRowClick, showAdColumns],
  )

  const showFullScreenTable = () => {
    showModal(
      <div className="flex h-[90vh] w-full max-w-full flex-col overflow-hidden pt-3">
        <AttributionTable
          fromDate={fromDate}
          toDate={toDate}
          integrationStatus={integrationStatus}
          locationIds={locationIds}
          sourceRows={sourceRows}
          mediumRows={mediumRows}
          sourceMediumRows={sourceMediumRows}
          campaignRows={campaignRows}
          adCampaignRows={adCampaignRows}
          adPlatformRows={adPlatformRows}
          channelRows={channelRows}
          reportedAttributionRows={reportedAttributionRows}
          enableExpand={false}
        />
      </div>,
      {
        title: `Table Breakdown`,
        removePadding: false,
        isFullScreen: true,
        overflowHidden: false,
        useRadix: true,
      },
    )
  }

  return (
    <SimpleTable
      key={tableFilter}
      className={enableExpand ? `w-full` : undefined}
      wrapperClassName={enableExpand ? `w-full` : undefined}
      data={tableData}
      columns={columns}
      extraRightSlot={
        <>
          <div className="flex items-center">
            <span className="mr-2 hidden text-ui-xs text-default md:inline">Rows:</span>
            <AttributionTableFilter defaultFilter={tableFilter} onFilterChange={handleTableFilterChange} />
          </div>
          {!!enableExpand && (
            <div className="btn btn-tertiary btn-sm cursor-pointer" onClick={showFullScreenTable}>
              <Expand className="h-4 w-4 text-strong" />
              <div className="ml-2 hidden text-ui-xs md:block">Expand</div>
            </div>
          )}
        </>
      }
      showColumnOrdering
      hideFooter
    />
  )
})
